import { CdkDragDrop, CdkDropList, CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, effect, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { digger } from '@cbre/digger';
import { GroupBase } from '../../../models/blueprint.model';
import { DataObject, ReportInfo, ReportRecord, ReportView, TemplateProjectionMap } from '../../../models/report.model';
import { TemplateField } from '../../../models/template.model';
import { ReportFieldFormatterService } from '../../../report-field-formatter.service';
import { onDrop } from '../presentation-report.helper';
import { PresentationDynamicViewHeaderComponent } from './record-header/record-header.component';
import { PresentationDynamicViewImageComponent } from './record-image/record-image.component';
import { NgClass, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { L10nTranslationModule } from 'angular-l10n';
import { FloorPlanComponent } from '../../presentation-view/record-body/floor-plan/floor-plan.component';

import { ReportFieldFormatterPipe } from '../../../shared/pipes/report-field-formatter.pipe';
import { ReportLabelFormatterPipe } from '../../../shared/pipes/report-label-formatter.pipe';
import { SelectTemplateOutletPipe } from '../../../shared/pipes/select-template-outlet.pipe';
import { IsFieldVisiblePipe } from '../../../shared/pipes/field-visible.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rep-presentation-dynamic-view-body',
  templateUrl: './record-body.component.html',
  styleUrl: './record-body.component.scss',
  imports: [
    PresentationDynamicViewHeaderComponent,
    PresentationDynamicViewImageComponent,
    NgClass,
    CdkDropList,
    CdkDrag,
    MatIcon,
    CdkDragHandle,
    NgTemplateOutlet,
    L10nTranslationModule,
    FloorPlanComponent,
    AsyncPipe,
    ReportFieldFormatterPipe,
    ReportLabelFormatterPipe,
    SelectTemplateOutletPipe,
    IsFieldVisiblePipe
],
})
export class PresentationDynamicViewBodyComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly reportFieldFormatterService = inject(ReportFieldFormatterService);

  readonly recordNumber = input.required<number>();
  readonly reportData = input.required<ReportInfo>();
  readonly currentView = input.required<ReportView>();
  readonly headerGroups = input.required<GroupBase<TemplateField>[]>();
  readonly bodyGroups = input.required<GroupBase<TemplateField>[]>();
  readonly imageGroups = input.required<GroupBase<TemplateField>[]>();
  readonly footerGroups = input.required<GroupBase<TemplateField>[]>();
  readonly reportLocale = input.required<number>();
  readonly templateProjectionMap = input.required<TemplateProjectionMap>();
  readonly removeRecord = output<ReportRecord>();
  readonly reportReordered = output<ReportRecord[]>();

  headerFields: TemplateField[];
  tableFields: GroupBase<TemplateField>;
  bodyAttributes: GroupBase<TemplateField>;
  footerAttributes: TemplateField[];
  allFields: TemplateField[];
  recordData: ReportRecord;
  templateTableConfigs: DataObject;
  tableData: any[];

  constructor() {
    effect(() => this.syncFromInputs());
  }

  private syncFromInputs(): void {
    const recordNumber = this.recordNumber();
    const reportData = this.reportData();
    const currentView = this.currentView();
    const bodyGroups = this.bodyGroups();
    const footerGroups = this.footerGroups();
    const headerGroups = this.headerGroups();

    this.tableFields = null;
    if (!!bodyGroups) {
      const [bodyAttributes, tableFields] = bodyGroups;
      this.tableFields = tableFields;
      const fields = bodyAttributes.fields.filter(field => {
        return this.reportFieldFormatterService.isFieldVisible(this.recordData, {
          templateId: currentView.templateId,
          field,
        });
      });
      this.bodyAttributes = { ...bodyAttributes, fields };
      this.allFields = [].concat(...(bodyGroups || []).map(group => group.fields));
    }
    if (!!reportData && !!reportData.records) {
      this.recordData = reportData.records[recordNumber - 1];
    }
    if (!!reportData && !!reportData.records && this.tableFields && this.tableFields.tableConfigs) {
      const data = digger(this.recordData, this.tableFields.tableConfigs.dataKey);
      // reviewing if data is an array, will prevent the table rows from throwing
      // errors when angular change detection gets triggered and data is an actual
      // object. (in HTML we have tableData.length)
      this.tableData = Array.isArray(data) ? data : null;
    }
    if (!!footerGroups) {
      this.footerAttributes = [].concat(...(footerGroups || []).map(group => group.fields));
    }

    this.headerFields = []
      .concat(...(headerGroups || []).map(group => group.fields))
      .sort((a, b) => a.options.headerSortOrder - b.options.headerSortOrder);
  }

  ngOnInit(): void {
    // required by angular-l10n
  }

  ngOnDestroy(): void {
    // required by angular-l10n
  }

  goToRecordDetails(recordData: DataObject) {
    // TODO (Ramakrishna): need to implement pipe as like in condensed view for link navigation
    const { entity } = this.reportData();
    const currentRecordId = recordData.entity.id;
    const url = `/records/${entity}/${currentRecordId}/details`;
    window.open(url, '_blank');
  }

  public onReorder(cdkDragDropEvent: CdkDragDrop<Array<{ [key: string]: any }>>, recordData) {
    onDrop(cdkDragDropEvent, recordData, this.reportData(), this.reportReordered, this.recordNumber());
  }
}
